// Login screen — full-viewport, logo + wordmark, username + password rows,
// square accent arrow button. Wrong credentials shake the form; 429 shows the
// brute-force lockout message.

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { apiFetch } from "../api/client";
import { Logo } from "./Logo";

import "./Login.css";

export function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await apiFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });

      if (res.ok) {
        const next = params.get("next");
        navigate(next && next.startsWith("/") ? next : "/", { replace: true });
        return;
      }

      // 429 = brute-force lockout (server-side per-IP throttle).
      setError(
        res.status === 429
          ? "too many attempts — try again later"
          : "wrong username, password or code",
      );
      setShaking(true);
      setPassword("");
      window.setTimeout(() => setShaking(false), 400);
    } catch {
      setError("network error — is the server running?");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-shell">
      <div className="login-logo-row">
        <span className="login-logo">
          <Logo size={44} />
        </span>
        <span className="login-wordmark">cinematic database</span>
      </div>
      <form
        className={`login-form${shaking ? " login-form--shake" : ""}`}
        onSubmit={onSubmit}
        autoComplete="off"
      >
        <label className="visually-hidden" htmlFor="login-username">
          Username
        </label>
        <div className="login-field">
          <input
            id="login-username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="username"
            autoFocus
            disabled={submitting}
            autoCapitalize="none"
            autoCorrect="off"
          />
        </div>
        <label className="visually-hidden" htmlFor="login-password">
          Password
        </label>
        <div className="login-field">
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="password / 6-digit code"
            disabled={submitting}
            aria-invalid={error != null}
            aria-describedby={error ? "login-error" : undefined}
          />
          <button
            type="submit"
            className="login-submit"
            disabled={submitting || username.length === 0 || password.length === 0}
            aria-label="Sign in"
          >
            →
          </button>
        </div>
        {error && (
          <div id="login-error" className="login-error" role="alert">
            {error}
          </div>
        )}
      </form>
    </main>
  );
}
